import React, {useState, useCallback} from 'react';
import {useParams} from 'react-router';
import styled from 'styled-components';
import {Loading} from '../../Components/Loading';
import {HowToUse} from '../../Components/HowToUse';
import {AstView} from 'rendition-core/types';
import {Header} from './Header';
import {CodeSandbox} from './CodeSandbox/CodeSandbox';
import {useStateWithUndo} from 'Hooks/useStateWithUndo';
import {useAutoSave} from 'Hooks/useAutoSave';
import {Builder} from './Builder/Builder';
import {Publish} from './Publish/Publish';
import {useDocumentActions} from './useDocumentActions';
import useLoadPreviewDocument from './useLoadDocument';
import {saveDocument} from 'Common/api';
import {useWebflowJSON} from './useWebflowJSON';
import {ProductContextProvider} from 'Contexts/ProductContext';
import {DocumentContextProvider} from 'Contexts/DocumentContext';
import {useQuery} from 'Hooks/useQuery';

const CODE = 'Code';
const STYLES = 'Styles';
const BUILDER = 'Builder';
const PUBLISH = 'Publish';
export type Mode = typeof CODE | typeof STYLES | typeof BUILDER | typeof PUBLISH;

export function Preview({mode = CODE}: {mode?: Mode}) {
    const query = useQuery();
    const [showModal, setShowModal] = useState(query.get('modal') === 'true');
    const {id} = useParams<{id: string}>();
    const [recordHistory, setRecordHistory] = useState(true);
    const [view, setView] = useStateWithUndo<AstView | null>(null, {disabled: !recordHistory});

    const saveVersion = useCallback(
        async (view: AstView) => {
            if (view) {
                await saveDocument(id, view);
            }
        },
        [id]
    );

    const webflowControls = useWebflowJSON(view, id, !!view);
    const {loading, setLoading, sandBoxLink, errored} = useLoadPreviewDocument({
        mode,
        id,
        view,
        setView,
        setWebflowJson: webflowControls.setWebflowJson,
    });

    const {state, save} = useAutoSave(view, saveVersion, !loading, 5000);
    const {cloneDoc, onGetCode} = useDocumentActions({
        view,
        setView,
        id,
        loading,
        setLoading,
        save,
    });

    if (loading) {
        return (
            <Root>
                <div style={{height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <Loading />
                </div>
            </Root>
        );
    }

    if (errored) {
        return (
            <Root>
                <div
                    style={{
                        color: 'white',
                        height: '100vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                    }}
                >
                    Sorry looks like something went wrong :(
                </div>
            </Root>
        );
    }

    return (
        <Root>
            <DocumentContextProvider documentId={id} saveDocument={save}>
                <ProductContextProvider view={view}>
                    {mode !== BUILDER && mode !== PUBLISH && (
                        <Header
                            id={id}
                            copyForWebFlow={webflowControls.webflowJson !== null && webflowControls.copyWebflowJson}
                            showDownloadAssets={mode === CODE}
                            cloneDoc={cloneDoc}
                            view={view}
                            saving={state}
                        />
                    )}
                    {showModal && (
                        <HowToUse
                            closeModal={() => {
                                setShowModal(false);
                            }}
                        />
                    )}
                    {mode === CODE && sandBoxLink && <CodeSandbox sandBoxLink={sandBoxLink} />}
                    {mode === BUILDER && view && (
                        <Builder
                            setRecordHistory={setRecordHistory}
                            saveStatus={state}
                            saveView={save}
                            setView={setView}
                            view={view}
                            webflowControls={webflowControls}
                            onGetCode={onGetCode}
                        />
                    )}
                    {mode === PUBLISH && view && <Publish documentId={id} save={save} setView={setView} view={view} />}
                </ProductContextProvider>
            </DocumentContextProvider>
        </Root>
    );
}

const Root = styled.div`
    background-color: #222222;
`;
